/**
 * video-editor/voice.js — запись голоса под ленту.
 *
 * Человек говорит, глядя на видео и слыша его звук. Всё, что здесь можно
 * сделать неправильно, уже сделано правильно в общих примитивах:
 *   - микрофон — MicManager (объяснение отказа, возврат контекста после
 *     окна разрешения). Обработка браузера выключена: эхоподавление вычитает
 *     звук страницы, а это ровно тоны, которыми строка синхронизации меряет
 *     петлю; по той же причине сделано у /vocal и /music-sequencer;
 *   - захват — RecordingLatency.capture: кадры графа со штампом, поэтому место
 *     дубля считается по часам графа, а не по тому, когда главный поток успел
 *     спросить время;
 *   - сдвиг — петля «колонки → микрофон» из общей строки <x-recording-sync>
 *     (замер или оценка): голос, сказанный под кадр, доезжает до графа позже
 *     на выход + вход, 60–230 мс у живых людей;
 *   - уровень — TakeLevel: тихий телефонный дубль поднимается к −9 dBFS.
 *
 * Где дубль встаёт. Пара «время графа ↔ время ленты» берётся на кадре превью
 * через ANCHOR_AFTER после пуска — когда элементы уже играют и часы превью
 * подтянулись к ведущему элементу. Первая выборка захвата (firstFrame / sr)
 * переводится в ленту этой парой и сдвигается на петлю; всё, что записано ДО
 * секунды пуска (отсчёт), отрезается с головы — `place()`.
 *
 * window.VideoEditorVoice.create({ L, ae, toast, ctx(), from(), play(from), stop(),
 *                                  onBegin(), onEnd(), onClose(), onTake(file, start, meta),
 *                                  ui: {pane, go, status, vu, row} })
 *   .open() · .begin() · .finish() · .close() · .tick(t) · .ended()
 *   .isActive() · .isRecording() · .phase()
 * window.VideoEditorVoice.place({...}) — чистая арифметика места дубля.
 */
(function () {
'use strict';
if (window.VideoEditorVoice) return;

var COUNT_IN = 3;          // с — отсчёт до пуска
var ANCHOR_AFTER = 0.5;    // с — пара часов берётся, когда превью уже идёт
var MIN_TAKE = 0.5;        // с — короче этого — не дубль
var MAX_TAKE = 30 * 60;    // с — потолок одного дубля (моно WAV 48 кГц ≈ 170 МБ)

/** Моно 16 бит PCM → WAV-файл: дубль хранится черновиком и уходит в экспорт как обычный звук. */
function wavFile(pcm, sr, name) {
    var n = pcm.length, buf = new ArrayBuffer(44 + n * 2), v = new DataView(buf);
    function str(o, s) { for (var i = 0; i < s.length; i++) v.setUint8(o + i, s.charCodeAt(i)); }
    str(0, 'RIFF'); v.setUint32(4, 36 + n * 2, true); str(8, 'WAVE');
    str(12, 'fmt '); v.setUint32(16, 16, true); v.setUint16(20, 1, true); v.setUint16(22, 1, true);
    v.setUint32(24, sr, true); v.setUint32(28, sr * 2, true); v.setUint16(32, 2, true); v.setUint16(34, 16, true);
    str(36, 'data'); v.setUint32(40, n * 2, true);
    for (var i = 0, o = 44; i < n; i++, o += 2) {
        var s = pcm[i] > 1 ? 1 : (pcm[i] < -1 ? -1 : pcm[i]);
        v.setInt16(o, s < 0 ? s * 0x8000 : s * 0x7fff, true);
    }
    return new File([buf], name, { type: 'audio/wav', lastModified: Date.now() });
}

/**
 * Где на ленте встаёт дубль и сколько выборок отрезать с головы.
 * ⚠️ Дубль, начатый с отсчётом, приходит ДО секунды пуска — его голова (то,
 * что микрофон слышал во время отсчёта) отрезается, и дубль встаёт ровно на
 * `from`. `Math.max(0, pos0)` вместо отрезания молча оставил бы отсчёт
 * в файле (та же ловушка, что у RecordingLatency.alignTake).
 * @param {{firstCtx:number, anchorCtx:number, anchorTl:number, latencyS:number,
 *          from:number, sr:number, length:number}} a
 * @returns {{start:number, drop:number, length:number}}
 */
function place(a) {
    var pos0 = a.anchorTl + (a.firstCtx - a.anchorCtx) - (a.latencyS || 0);
    var drop = Math.max(0, Math.round((a.from - pos0) * a.sr));
    drop = Math.min(drop, a.length);
    return { start: drop > 0 ? a.from : Math.max(0, pos0), drop: drop, length: a.length - drop };
}

function fmt(s) {
    s = Math.max(0, s || 0);
    var m = Math.floor(s / 60), r = Math.floor(s % 60);
    return m + ':' + (r < 10 ? '0' : '') + r;
}

function create(o) {
    var L = o.L || {}, ui = o.ui || {};
    var st = null, mic = null, sync = null, takes = 0;

    function ae(f, extra) { try { if (o.ae) o.ae(f, extra); } catch (e) {} }
    function toast(msg, type) { try { if (o.toast && msg) o.toast(msg, type); } catch (e) {} }
    function say(text) { if (ui.status) ui.status.textContent = text || ''; }
    function recording(s) { return !!s && (s.phase === 'countin' || s.phase === 'rec'); }

    function render() {
        var s = st, ph = s ? s.phase : 'closed';
        if (ui.pane) ui.pane.classList.toggle('is-rec', recording(s));
        if (ui.go) {
            ui.go.disabled = !(ph === 'ready' || recording(s));
            ui.go.textContent = recording(s) ? '■ ' + (L.voice_stop || 'Stop') : '● ' + (L.voice_go || 'Record');
            ui.go.classList.toggle('is-rec', recording(s));
        }
        if (ph === 'mic') say(L.voice_mic_wait);
        else if (ph === 'ready' || ph === 'closed') say('');
        else if (ph === 'countin') say(String(L.voice_countin || ':n').replace(':n', String(s.count)));
        else if (ph === 'saving') say('…');
        if (ui.vu && !(ph === 'ready' || recording(s))) ui.vu.style.width = '0%';
    }

    function pumpVu(s) {
        if (st !== s || !s.an) return;
        var buf = s.vuBuf || (s.vuBuf = new Uint8Array(s.an.fftSize));
        s.an.getByteTimeDomainData(buf);
        var peak = 0;
        for (var i = 0; i < buf.length; i++) { var x = Math.abs(buf[i] - 128) / 128; if (x > peak) peak = x; }
        if (ui.vu) ui.vu.style.width = Math.min(100, peak * 140) + '%';
        s.vuRaf = requestAnimationFrame(function () { pumpVu(s); });
    }

    function bindSync() {
        if (!ui.row || !window.RecordingLatency || typeof window.RecordingLatency.bindRow !== 'function') return;
        if (!sync) {
            sync = window.RecordingLatency.bindRow(ui.row, {
                slug: 'video-editor',
                getCtx: function () { return o.ctx(); },
                getMicStream: function () { return st && st.stream; },
                onChange: function (ms) { if (st) st.latencyMs = ms; },
                isBusy: function () { return recording(st); },
            });
        }
        try { sync.refresh(); if (st) st.latencyMs = sync.get().ms; } catch (e) {}
    }

    /** Открыть микрофон и показать «Записать». */
    async function open() {
        if (st) return;
        var s = st = { phase: 'mic', from: 0, stream: null, src: null, an: null, cap: null, timers: [],
            latencyMs: 0, anchor: null, last: null, startWall: 0, count: COUNT_IN, vuRaf: 0 };
        render();
        var ctx = o.ctx();
        if (!ctx || !window.MicManager || !window.RecordingLatency || typeof window.RecordingLatency.capture !== 'function') {
            toast(L.voice_unsupported, 'error');
            ae('voice_fail', { err: 'unsupported' });
            close();
            return;
        }
        try { if (ctx.state !== 'running') ctx.resume(); } catch (e) {}
        if (!mic) {
            mic = new window.MicManager({ slug: 'video-editor', locale: window.__LOCALE });
            try { if (mic.bindContext) mic.bindContext(function () { return o.ctx(); }); } catch (e) {}
        }
        try {
            var stream = await mic.startRaw({ audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false, channelCount: { ideal: 1 } } });
            if (st !== s) { try { stream.getTracks().forEach(function (tr) { tr.stop(); }); } catch (e) {} return; }
            s.stream = stream;
            s.src = ctx.createMediaStreamSource(stream);
            s.an = ctx.createAnalyser();
            s.an.fftSize = 1024;
            s.src.connect(s.an);          // только индикатор: в колонки микрофон не идёт
            pumpVu(s);
            bindSync();
            s.phase = 'ready';
            render();
            ae('voice_ready', { lat: Math.round(s.latencyMs), kind: sync ? sync.get().kind : 'none' });
        } catch (err) {
            // Отказ MicManager уже объяснил своим окном.
            if (st === s) {
                ae('voice_fail', { err: String((err && (err.code || err.name)) || 'mic').slice(0, 40) });
                close();
            }
        }
    }

    /** «Записать»: захват, отсчёт, пуск ленты с ползунка. */
    function begin() {
        var s = st;
        if (!s || s.phase !== 'ready') return;
        var ctx = o.ctx();
        s.from = Math.max(0, o.from());
        s.phase = 'countin';
        s.count = COUNT_IN;
        s.anchor = null; s.last = null;
        render();
        if (o.onBegin) o.onBegin();
        window.RecordingLatency.capture(ctx, s.stream).then(function (cap) {
            if (st !== s || s.phase !== 'countin') { try { cap.stop(); } catch (e) {} return; }
            s.cap = cap;
            var iv = setInterval(function () {
                if (st !== s || s.phase !== 'countin') { clearInterval(iv); return; }
                s.count--;
                if (s.count > 0) { render(); return; }
                clearInterval(iv);
                s.phase = 'rec';
                s.startWall = performance.now();
                o.play(s.from);
                say('● ' + String(L.voice_recording || ':time').replace(':time', fmt(0)));
                render();
                ae('voice_begin', { lat: Math.round(s.latencyMs), kind: sync ? sync.get().kind : 'none' });
                s.timers.push(setTimeout(function () { if (st === s && s.phase === 'rec') finish(); }, MAX_TAKE * 1000));
            }, 1000);
            s.timers.push(iv);
        }).catch(function () {
            if (st !== s) return;
            toast(L.voice_failed, 'error');
            ae('voice_fail', { err: 'capture' });
            close();
        });
    }

    /** Кадр превью во время записи: пара часов и счётчик. */
    function tick(t) {
        var s = st;
        if (!s || s.phase !== 'rec') return;
        var pair = { ctx: o.ctx().currentTime, tl: t };
        s.last = pair;
        if (!s.anchor && performance.now() - s.startWall >= ANCHOR_AFTER * 1000) s.anchor = pair;
        say('● ' + String(L.voice_recording || ':time').replace(':time', fmt(t - s.from)));
    }

    /** «Стоп»: дубль — на дорожку, микрофон отпускается. */
    function finish() {
        var s = st;
        if (!s) return;
        if (s.phase === 'countin') { cancelTake(); return; }
        if (s.phase !== 'rec') return;
        s.phase = 'saving';
        render();
        o.stop();
        var res = null;
        try { res = s.cap ? s.cap.stop() : null; } catch (e) { res = null; }
        s.cap = null;
        var ctx = o.ctx(), sr = ctx.sampleRate;
        var anchor = s.anchor || s.last;
        if (!res || !res.pcm || !res.pcm.length || !anchor || !(res.firstFrame >= 0)) {
            toast(L.voice_failed, 'error');
            ae('voice_fail', { err: !anchor ? 'no_anchor' : 'empty' });
            close();
            return;
        }
        var p = place({ firstCtx: res.firstFrame / sr, anchorCtx: anchor.ctx, anchorTl: anchor.tl,
            latencyS: (s.latencyMs || 0) / 1000, from: s.from, sr: sr, length: res.pcm.length });
        if (p.length < MIN_TAKE * sr) {
            toast(L.voice_too_short, 'warning');
            ae('voice_fail', { err: 'too_short' });
            s.phase = 'ready';
            render();
            if (o.onEnd) o.onEnd();
            return;
        }
        var buf = ctx.createBuffer(1, p.length, sr);
        buf.getChannelData(0).set(res.pcm.subarray(p.drop, p.drop + p.length));
        var gainDb = 0, peakDb = null;
        if (window.TakeLevel) {
            try {
                var lv = window.TakeLevel.analyze(buf);
                peakDb = Math.round(lv.peakDb);
                gainDb = window.TakeLevel.normalizeGainDb(lv);
                if (gainDb > 0) window.TakeLevel.applyGain(buf, gainDb);
            } catch (e) { gainDb = 0; }
        }
        takes++;
        var name = (L.voice_take || 'Voice') + ' ' + takes;
        var file = wavFile(buf.getChannelData(0), sr, name + '.wav');
        ae('voice_added', { dur: Math.round(p.length / sr * 10) / 10, lat: Math.round(s.latencyMs),
            kind: sync ? sync.get().kind : 'none', precise: res.precise ? 1 : 0, gain: Math.round(gainDb),
            peak: peakDb, head: Math.round(p.drop / sr * 1000) });
        close();
        o.onTake(file, p.start, { name: name, duration: p.length / sr });
    }

    /** «Стоп» во время отсчёта: запись не начиналась — снова «готов». */
    function cancelTake() {
        var s = st;
        if (!s) return;
        try { if (s.cap) s.cap.stop(); } catch (e) {}
        s.cap = null;
        s.timers.forEach(function (x) { clearTimeout(x); clearInterval(x); });
        s.timers = [];
        s.phase = 'ready';
        render();
        ae('voice_cancel', { phase: 'countin' });
        if (o.onEnd) o.onEnd();
    }

    /** Закрыть: микрофон отпускается — индикатор записи в браузере гаснет. */
    function close() {
        var s = st;
        if (!s) return;
        var ph = s.phase;
        st = null;
        if (s.vuRaf) cancelAnimationFrame(s.vuRaf);
        s.timers.forEach(function (x) { clearTimeout(x); clearInterval(x); });
        try { if (s.cap) s.cap.stop(); } catch (e) {}
        try { if (s.src) s.src.disconnect(); } catch (e) {}
        try { if (s.stream) s.stream.getTracks().forEach(function (tr) { tr.stop(); }); } catch (e) {}
        try { if (mic) mic.stop(); } catch (e) {}
        if (ph === 'countin' || ph === 'rec') { try { o.stop(); } catch (e) {} ae('voice_cancel', { phase: ph }); }
        render();
        if ((ph === 'countin' || ph === 'rec' || ph === 'saving') && o.onEnd) o.onEnd();
        if (o.onClose) o.onClose();
    }

    return {
        open: open, begin: begin, finish: finish, close: close, tick: tick,
        /** Лента доиграла до конца — дубль заканчивается там же. */
        ended: function () { if (st && st.phase === 'rec') finish(); },
        isActive: function () { return !!st; },
        isRecording: function () { return recording(st); },
        phase: function () { return st ? st.phase : 'closed'; },
    };
}

var api = { create: create, place: place, _wavFile: wavFile };
if (typeof module !== 'undefined' && module.exports) module.exports = api;
window.VideoEditorVoice = api;
})();
